<script setup lang="ts">
// TODO :: TASK-2103 - Should be promoted to a molecule
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { applyShelterCurrency } from '@Helpers';

defineExpose({ closeEdit: onCloseEdit });

const props = withDefaults(
  defineProps<{
    closeOnConfirm?: boolean;
    currentAmount: number;
    hidePencil?: boolean;
    isEditingAnimals: boolean;
    label: string;
    originalAmount?: number | null;
    quantity?: number;
    stacked?: boolean;
    startInEditMode?: boolean;
    unitLabel?: string;
    updating?: boolean;
  }>(),
  {
    closeOnConfirm: true,
    hidePencil: false,
    originalAmount: null,
    quantity: undefined,
    stacked: false,
    startInEditMode: false,
    unitLabel: undefined,
    updating: false,
  }
);

const emit = defineEmits<{
  close: [];
  onUpdateRequest: [payload: { amount: number; discountType: 'percent' | 'fixed'; discount?: number }];
}>();

const { locale } = useI18n();

const MODE_OPTIONS = [
  { value: 'dollar' as const, label: '$' },
  { value: 'percentage' as const, label: '%' },
];

const dollarValue = ref(props.currentAmount);
const isEditing = ref(false);
const mode = ref<'dollar' | 'percentage'>('dollar');
const percentValue = ref(0);

const editModeReferenceAmount = computed(() => applyShelterCurrency(props.originalAmount ?? props.currentAmount));
const inputLocale = computed(() => (locale.value === 'pt-BR' ? 'pt-BR' : 'en-US'));
const isPerUnitMode = computed(() => props.unitLabel != null && props.quantity != null && props.quantity >= 1);

const formattedCurrent = computed(() => {
  const amount = isPerUnitMode.value ? props.currentAmount * props.quantity! : props.currentAmount;

  return applyShelterCurrency(amount);
});

const formattedOriginal = computed(() => {
  if (props.originalAmount == null) return '';

  const amount = isPerUnitMode.value ? props.originalAmount * props.quantity! : props.originalAmount;

  return applyShelterCurrency(amount);
});

const liveEditTotal = computed(() => {
  if (!isPerUnitMode.value) return props.currentAmount;

  if (mode.value === 'dollar') {
    const perUnit = Math.max(0, Number(dollarValue.value ?? 0));

    return Math.round(perUnit * props.quantity! * 100) / 100;
  }

  const percent = Math.min(100, Math.max(0, Number(percentValue.value ?? 0)));
  const total = props.currentAmount * props.quantity!;

  return Math.round(total * (1 - percent / 100) * 100) / 100;
});

const showOriginalAndDiscounted = computed(
  () => props.originalAmount != null && props.originalAmount > props.currentAmount
);

watch(
  () => props.currentAmount,
  (newAmount) => (dollarValue.value = newAmount),
  { immediate: true }
);

watch(
  () => props.startInEditMode,
  (start) => (start ? openEdit() : isEditing.value && onCloseEdit()),
  { immediate: true }
);

// EVENTS
function onCloseEdit() {
  isEditing.value = false;
}

function onCancelClick() {
  onCloseEdit();
  emit('close');
}

function openEdit() {
  isEditing.value = true;
  dollarValue.value = props.currentAmount;
  percentValue.value = 0;
  mode.value = 'dollar';
}

function onConfirm() {
  let amount = 0;

  const isDollarMode = mode.value === 'dollar';
  const isPercentageMode = mode.value === 'percentage';

  if (isPerUnitMode.value) {
    if (isDollarMode) {
      const perUnit = Math.max(0, Number(dollarValue.value ?? 0));
      amount = Math.round(perUnit * props.quantity! * 100) / 100;
    }

    if (isPercentageMode) {
      const percent = Math.min(100, Math.max(0, Number(percentValue.value ?? 0)));
      const total = props.currentAmount * props.quantity!;
      amount = Math.round(total * (1 - percent / 100) * 100) / 100;
    }
  }

  if (!isPerUnitMode.value) {
    if (isDollarMode) {
      amount = Math.max(0, Number(dollarValue.value ?? 0));
    }

    if (isPercentageMode) {
      const percent = Math.min(100, Math.max(0, Number(percentValue.value ?? 0)));
      amount = Math.round(props.currentAmount * (1 - percent / 100) * 100) / 100;
    }
  }

  const payload: { amount: number; discountType: 'percent' | 'fixed'; discount?: number } = {
    amount,
    discountType: isPercentageMode ? 'percent' : 'fixed',
  };
  if (isPercentageMode) {
    payload.discount = Math.min(100, Math.max(0, Number(percentValue.value ?? 0)));
  }
  emit('onUpdateRequest', payload);

  if (props.closeOnConfirm) onCloseEdit();
}
</script>

<template>
  <div
    :class="[
      'flex justify-start gap-2 lg:items-center',
      isEditing
        ? 'w-full flex-col lg:flex-row lg:justify-end 2xl:h-8 2xl:flex-row 2xl:flex-nowrap 2xl:items-center 2xl:justify-between'
        : 'flex-row justify-between',
      !stacked ? 'lg:justify-between' : '',
    ]"
  >
    <span
      v-if="label"
      class="shrink-0"
    >
      {{ $t(label) }}
    </span>

    <div
      v-if="isEditing"
      :class="[
        'flex lg:w-auto',
        stacked
          ? 'w-full flex-row flex-wrap items-center justify-between gap-2 lg:justify-end lg:gap-4 xl:w-full xl:flex-nowrap'
          : 'w-full flex-row items-center gap-0',
      ]"
      @keydown.enter.prevent="onConfirm"
      @keydown.esc="onCancelClick"
    >
      <div
        :class="[
          'flex items-end',
          stacked
            ? 'my-1 w-fit flex-nowrap gap-2 lg:w-auto lg:shrink-0'
            : 'w-full gap-3 lg:flex-row lg:flex-nowrap lg:gap-2',
        ]"
      >
        <SelectButton
          v-model="mode"
          class="flex [&_.p-button.p-highlight]:text-white before:[&_.p-button.p-highlight]:bg-brand-600"
          pt:button="h-9"
          optionValue="value"
          optionLabel="label"
          :allowEmpty="false"
          :options="MODE_OPTIONS"
          :disabled="updating"
        />

        <div class="mt relative mt-3.5 lg:mt-0">
          <span
            class="absolute -top-[18px] left-1/2 z-10 h-fit w-[120px] -translate-x-1/2 bg-white px-1 text-center text-xs text-gray-500"
          >
            {{ $t('cart.summary.originalAmount', { amount: editModeReferenceAmount }) }}
          </span>

          <template v-if="mode === 'dollar'">
            <InputNumber
              v-model="dollarValue"
              currency="USD"
              mode="currency"
              pt:input:root="text-sm h-9 min-w-24 w-28 text-center"
              :locale="inputLocale"
              :minFractionDigits="2"
              :maxFractionDigits="2"
              :disabled="updating"
            />

            <span
              v-if="unitLabel"
              class="absolute -bottom-4 right-2.5 text-xs text-gray-500"
            >
              {{ unitLabel }}
            </span>
          </template>

          <div
            v-else
            class="relative"
          >
            <InputNumber
              v-model="percentValue"
              pt:input:root="text-sm h-9 min-w-24 w-28 pl-[20px] pr-[50px] text-center"
              :min="0"
              :max="100"
              :minFractionDigits="0"
              :maxFractionDigits="1"
              :disabled="updating"
            />

            <span class="absolute right-[10px] top-2 w-12 text-sm text-gray-500">
              {{ $t('cart.summary.percentOff') }}
            </span>
          </div>
        </div>

        <span
          v-if="!stacked && isPerUnitMode"
          class="flex min-w-[5rem] shrink-0 flex-col items-end text-right text-sm text-gray-600"
        >
          {{ applyShelterCurrency(liveEditTotal) }}
        </span>
      </div>

      <div
        v-if="stacked"
        class="flex w-fit items-center gap-0 lg:w-fit"
      >
        <slot name="beforeTotal" />

        <span
          v-if="isPerUnitMode"
          class="flex min-w-[5rem] shrink-0 flex-col items-end text-right text-sm text-gray-600"
        >
          {{ applyShelterCurrency(liveEditTotal) }}
        </span>

        <div class="flex gap-0">
          <AShoppingCartIconButton
            icon="pi pi-check"
            compact
            :disabled="updating"
            customClass="text-brand-500"
            @onClick="onConfirm"
          />

          <AShoppingCartIconButton
            icon="pi pi-times"
            compact
            :ariaLabel="$t('cart.summary.ariaCancelEdit')"
            :disabled="updating"
            customClass="text-red-600"
            @onClick="onCancelClick"
          />
        </div>
      </div>

      <div
        v-if="!stacked"
        class="flex w-full items-center gap-2 lg:gap-0"
      >
        <slot name="beforeTotal" />

        <span
          v-if="isPerUnitMode"
          class="flex min-w-[5rem] shrink-0 flex-col items-end text-right text-sm text-gray-600"
        >
          {{ applyShelterCurrency(liveEditTotal) }}
        </span>

        <div class="flex gap-0">
          <AShoppingCartIconButton
            icon="pi pi-check"
            compact
            :disabled="updating"
            customClass="text-brand-500"
            @onClick="onConfirm"
          />

          <AShoppingCartIconButton
            icon="pi pi-times"
            compact
            :ariaLabel="$t('cart.summary.ariaCancelEdit')"
            :disabled="updating"
            customClass="text-red-600"
            @onClick="onCancelClick"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex w-full items-center justify-end gap-2 lg:w-auto"
    >
      <AShoppingCartAmountDisplay
        :amount="formattedCurrent"
        :originalAmount="showOriginalAndDiscounted ? formattedOriginal : null"
        :hasDiscount="showOriginalAndDiscounted"
        wrapperClass="flex flex-col items-end text-right"
      />

      <AShoppingCartIconButton
        v-if="!hidePencil && isEditingAnimals"
        icon="pi pi-pencil"
        :ariaLabel="$t('cart.summary.ariaEditAmount')"
        @onClick="openEdit"
      />
    </div>
  </div>
</template>
